import React, { useState, useEffect } from "react";
import { CheckCircle, Calendar, Clock, Download, XCircle } from "lucide-react";

/**
 * Bestätigungsseite nach erfolgreicher Verschiebung
 */
const AppointmentRescheduled = () => {
  const rootElement = document.getElementById("appointment-rescheduled-root");
  const appointmentId = rootElement?.dataset.appointmentId;
  const userSID = rootElement?.dataset.userSid;

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAppointment();
  }, [appointmentId, userSID]);

  const loadAppointment = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/index.php/customer/getAppointmentInfo?aID=${appointmentId}&uSID=${userSID}`
      );
      const data = await response.json();

      if (data.success) {
        setAppointment(data.appointment);
      } else {
        setError(data.error || "Termin konnte nicht geladen werden");
      }
    } catch (error) {
      console.error("Fehler:", error);
      setError("Ein Fehler ist aufgetreten");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadICS = () => {
    const icsUrl = `/storage/${userSID}/appointments/${appointment.appointment_calendar_id}/${appointment.booking_id}.ics`;
    window.location.href = icsUrl;
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
          <p className="text-muted">Lade Informationen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div
          className="alert alert-danger d-flex align-items-center"
          role="alert"
        >
          <XCircle size={24} className="me-2" />
          <div>
            <h5 className="alert-heading mb-1">Fehler</h5>
            <p className="mb-0">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: "800px" }}>
      <div className="py-5">
        {/* Erfolgs-Header */}
        <div className="text-center mb-5">
          <div className="mb-4">
            <CheckCircle size={80} className="text-success" />
          </div>
          <h1 className="display-5 fw-bold mb-3">
            Termin erfolgreich verschoben!
          </h1>
          <p className="lead text-muted">
            Ihr Termin wurde auf das neue Datum verschoben.
          </p>
        </div>

        {/* Neue Termindetails */}
        <div
          className="card shadow-sm mb-4"
          style={{ borderLeft: "4px solid #10b981" }}
        >
          <div className="card-body">
            <h5 className="card-title mb-3 text-success">✓ Neuer Termin</h5>
            <div className="row g-3">
              <div className="col-md-4">
                <div className="d-flex align-items-center">
                  <Calendar size={18} className="me-2 text-primary" />
                  <div>
                    <small className="text-muted d-block">Termintyp</small>
                    <strong>{appointment?.type_name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex align-items-center">
                  <Calendar size={18} className="me-2 text-primary" />
                  <div>
                    <small className="text-muted d-block">Datum</small>
                    <strong>
                      {appointment?.appointment_date &&
                        new Date(
                          ...appointment.appointment_date
                            .split("-")
                            .map((n, i) => (i === 1 ? n - 1 : n))
                        ).toLocaleDateString("de-DE", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                    </strong>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex align-items-center">
                  <Clock size={18} className="me-2 text-primary" />
                  <div>
                    <small className="text-muted d-block">Uhrzeit</small>
                    <strong>
                      {appointment?.start_time?.substring(0, 5)} -{" "}
                      {appointment?.end_time?.substring(0, 5)} Uhr
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            {appointment?.duration_minutes && (
              <div className="mt-3 pt-3 border-top">
                <small className="text-muted">
                  <Clock size={14} className="me-1" />
                  Dauer: <strong>{appointment.duration_minutes} Minuten</strong>
                </small>
              </div>
            )}
          </div>
        </div>

        {/* Informationen */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">📧 Was passiert jetzt?</h5>
            <ul className="mb-0">
              <li className="mb-2">
                Sie erhalten in Kürze eine <strong>E-Mail-Bestätigung</strong>{" "}
                mit den neuen Termindaten
              </li>
              <li className="mb-2">
                Der Termin wurde in unserem Kalender aktualisiert
              </li>
              <li>
                Sie können den Termin jederzeit in Ihren Kalender importieren
              </li>
            </ul>
          </div>
        </div>

        {/* Kalender-Download */}
        <div
          className="card shadow-sm mb-4"
          style={{ backgroundColor: "#e7f3ff" }}
        >
          <div className="card-body">
            <h5 className="card-title mb-3">
              📅 Termin zum Kalender hinzufügen
            </h5>
            <p className="text-muted mb-3">
              Laden Sie die ICS-Datei herunter und importieren Sie den Termin in
              Ihren Kalender (Outlook, Google Calendar, Apple Calendar, etc.)
            </p>
            <button onClick={handleDownloadICS} className="btn btn-primary">
              <Download size={18} className="me-2" />
              ICS-Datei herunterladen
            </button>
          </div>
        </div>

        {/* Weitere Aktionen */}
        <div
          className="card shadow-sm mb-4"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <div className="card-body">
            <h5 className="card-title mb-3">Weitere Optionen</h5>
            <div className="d-flex gap-2 flex-wrap">
              <a
                href={`/index.php/customer/edit?aID=${appointmentId}&uSID=${userSID}`}
                className="btn btn-outline-success"
              >
                <Calendar size={18} className="me-2" />
                Termin-Daten bearbeiten
              </a>
              <a
                href={`/index.php/customer/reschedule?aID=${appointmentId}&uSID=${userSID}`}
                className="btn btn-outline-primary"
              >
                <Calendar size={18} className="me-2" />
                Termin erneut verschieben
              </a>
              <a
                href={`/index.php/customer/cancel?aID=${appointmentId}&uSID=${userSID}`}
                className="btn btn-outline-danger"
              >
                Termin stornieren
              </a>
            </div>
          </div>
        </div>

        {/* Zurück zum Kalender */}
        {appointment?.calendar_uuid && (
          <div className="text-center mb-4">
            <a
              href={`/index.php/customer/appointment?calendar_id=${appointment.calendar_uuid}&userSID=${userSID}`}
              className="btn btn-lg btn-primary"
            >
              Zurück zum Buchungskalender
              <Calendar size={18} className="ms-2" />
            </a>
          </div>
        )}

        {/* Footer-Info */}
        <div className="text-center text-muted">
          <small>
            Bei Fragen zu Ihrem Termin kontaktieren Sie uns bitte unter{" "}
            <a href={`mailto:${appointment?.photographer_email}`}>
              {appointment?.photographer_email}
            </a>
          </small>
        </div>
      </div>
    </div>
  );
};

export default AppointmentRescheduled;
